import React, { useMemo, useRef, useCallback, useLayoutEffect } from 'react';
import ChatBubble from './ChatBubble';
import EmptyState from '../data-display/EmptyState';
import { ChatIcon } from '../Icons/svgIcons';
import DateSeparator from './DateSeparator';

const getTimestampDate = (ts) => {
  if (!ts) return new Date();
  if (typeof ts.toDate === 'function') {
    return ts.toDate();
  }
  if (ts.seconds !== undefined) {
    return new Date(ts.seconds * 1000 + (ts.nanoseconds || 0) / 1000000);
  }
  const d = new Date(ts);
  return isNaN(d.getTime()) ? new Date() : d;
};

const getTimestampMs = (timestamp) => {
  return getTimestampDate(timestamp).getTime();
};

const isDifferentDay = (ts1, ts2) => {
  const dateA = getTimestampDate(ts1);
  const dateB = getTimestampDate(ts2);
  return (
    dateA.getDate() !== dateB.getDate() ||
    dateA.getMonth() !== dateB.getMonth() ||
    dateA.getFullYear() !== dateB.getFullYear()
  );
};

/**
 * ChatMessageList — the scrollable body of the chat view.
 *
 * Handles loading / empty / no-results / message states and renders
 * each ChatBubble with all the selection + action-menu props wired up.
 * Supports infinite-scroll pagination via loadMore / hasMore / loadingOlder.
 */
const ChatMessageList = ({
  isLoading,
  messages,
  filteredMessages,
  searchQuery,
  chatBgStyle,
  // Per-bubble props
  currentUserUid,
  chatCustomization,
  senderAvatar,
  receiverAvatar,
  // Selection
  isSelectMode,
  selectedMessageIds,
  activeActionMessageId,
  // Handlers
  onEditRequest,
  onDelete,
  onLongPress,
  onSelectToggle,
  onToggleActions,
  // Ref for auto-scroll
  messagesEndRef,
  // Pagination
  loadingOlder,
  hasMore,
  loadMore,
}) => {
  const scrollContainerRef = useRef(null);
  const prevScrollHeightRef = useRef(0);

  const sortedFilteredMessages = useMemo(() => {
    return [...filteredMessages].sort((a, b) => getTimestampMs(a.timestamp) - getTimestampMs(b.timestamp));
  }, [filteredMessages]);

  // Preserve scroll position when older messages are prepended
  useLayoutEffect(() => {
    if (scrollContainerRef.current && prevScrollHeightRef.current > 0 && !loadingOlder) {
      const newScrollHeight = scrollContainerRef.current.scrollHeight;
      if (newScrollHeight > prevScrollHeightRef.current) {
        scrollContainerRef.current.scrollTop = newScrollHeight - prevScrollHeightRef.current;
        prevScrollHeightRef.current = 0;
      }
    }
  }, [messages, loadingOlder]);

  const handleScroll = useCallback((e) => {
    if (e.currentTarget.scrollTop <= 10 && hasMore && !loadingOlder) {
      prevScrollHeightRef.current = e.currentTarget.scrollHeight;
      loadMore();
    }
  }, [hasMore, loadingOlder, loadMore]);

  const handleLoadMoreClick = useCallback(() => {
    if (scrollContainerRef.current) {
      prevScrollHeightRef.current = scrollContainerRef.current.scrollHeight;
    }
    loadMore();
  }, [loadMore]);

  return (
    <div
      ref={scrollContainerRef}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto px-4 py-4 relative"
      style={chatBgStyle}
    >
      {isLoading ? (
        <div className="flex items-center justify-center h-full">
          <div className="w-8 h-8 border-2 border-[var(--btn-primary-bg)] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : messages.length === 0 ? (
        <EmptyState
          icon={<ChatIcon className="w-14 h-14" />}
          title="No messages yet"
          message="Start the conversation!"
        />
      ) : sortedFilteredMessages.length === 0 && searchQuery.trim() !== '' ? (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <h2 className="text-gray-700 font-semibold">No messages found</h2>
          <p className="text-gray-500 text-sm mt-1">Try a different keyword.</p>
        </div>
      ) : (
        <>
          {/* Older messages loader / button */}
          {loadingOlder && (
            <div className="flex justify-center py-3">
              <div className="bg-white/80 backdrop-blur-sm px-4 py-1.5 rounded-full shadow-sm border border-gray-100 text-xs text-gray-500 font-medium flex items-center gap-2">
                <svg className="animate-spin h-3.5 w-3.5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Loading older messages...
              </div>
            </div>
          )}

          {hasMore && !loadingOlder && (
            <div className="flex justify-center py-3">
              <button
                onClick={handleLoadMoreClick}
                className="text-xs bg-white/80 backdrop-blur-sm text-blue-600 px-4 py-2 rounded-full font-medium shadow-sm border border-blue-100 active:bg-blue-50 transition-colors"
              >
                Load older messages
              </button>
            </div>
          )}

          {sortedFilteredMessages.map((message, index) => {
            const prevMsg = index > 0 ? sortedFilteredMessages[index - 1] : null;
            const showDateSeparator = !prevMsg || isDifferentDay(prevMsg.timestamp, message.timestamp);

            const msgDate = getTimestampDate(message.timestamp);

            return (
              <React.Fragment key={message.id}>
                {showDateSeparator && !isNaN(msgDate.getTime()) && (
                  <DateSeparator date={msgDate} />
                )}
                <ChatBubble
                  message={message}
                  currentUserUid={currentUserUid}
                  customization={chatCustomization}
                  senderAvatar={senderAvatar}
                  receiverAvatar={receiverAvatar}
                  onEditRequest={onEditRequest}
                  onDelete={onDelete}
                  isSelectMode={isSelectMode}
                  isSelected={selectedMessageIds.has(message.id)}
                  onLongPress={onLongPress}
                  onSelectToggle={onSelectToggle}
                  showActions={activeActionMessageId === message.id}
                  onToggleActions={onToggleActions}
                  selectedCount={selectedMessageIds.size}
                />
              </React.Fragment>
            );
          })}
          <div ref={messagesEndRef} />
        </>
      )}
    </div>
  );
};

export default ChatMessageList;
